// eslint-disable-next-line max-classes-per-file
import './bootstrap'
import React from 'react'
import ReactDOM from 'react-dom'
import { createInertiaApp } from '@inertiajs/react'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import '@v4/utils/validations'
import { BUILDER_VERSION } from '@v4/constants/general'

import { checkIsBuilderMode } from '@v4/utils/general'
import { createBuilderStore } from '@v4/store/builderStore'
import AppLayout from '@v4/components/common/AppLayout'
import { createAppModel } from '@v4/store/appStore'
import { BrowserRouter } from 'react-router-dom'
import { createGenerateClassName, StylesProvider } from '@material-ui/core/styles'

Bugsnag.start({
	apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
	plugins: [new BugsnagPluginReact()],
	releaseStage: window.initialData.bug_tracking ? import.meta.env.VITE_BUGSNAG_RELEASE_STAGE : 'null',
	enabledReleaseStages: ['production', 'staging'],
	onError: event => {
		const message = event.originalError?.message || ''
		return (
			// eslint-disable-next-line no-underscore-dangle
			!event._metadata?.['unhandledrejection handler'] &&
			!new RegExp('fbq', 'gi').test(message) &&
			!new RegExp('ResizeObserver loop limit exceeded', 'gi').test(message)
		)
	},
})

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

const rootElement = document.getElementById('root')

if (rootElement) {
	rootElement.dataset.page = JSON.stringify({})
}

class FrameState {
	constructor({ active = false, root = rootElement, version, initialScroll }) {
		const state = { active, version, initialScroll }

		this.root = root
		this.state = state
		this.handleMount(state)
	}

	initApp(version, initialScroll) {
		const {
			component,
			props: { data },
		} = window.pageData

		const builderStore = window?.builderStore || createBuilderStore(data)

		createInertiaApp({
			id: this.root.id,
			resolve: () => {
				if (version !== BUILDER_VERSION) {
					return resolvePageComponent(
						'/resources/react/v4/components/common/VersionError/index.js',
						import.meta.glob('/resources/react/v4/components/common/VersionError/index.js'),
					)
				}

				return resolvePageComponent(
					`/resources/react/v4/components/pages/${component}/${component}.jsx`,
					import.meta.glob('/resources/react/v4/components/pages/*/*.jsx'),
				).then(async module => {
					const appModel = createAppModel(data, component)

					const { SectionManager } = module.default

					if (SectionManager) {
						const promises = data.page.sections.map(section => {
							const [group, key] = section.key.split('/')

							const variant = _.get(data.template, [
								'section_groups',
								group,
								'sections',
								key,
								'theme',
								'variant',
							])

							return SectionManager.load(group, variant)
						})

						await Promise.all(promises)
					}

					// eslint-disable-next-line no-param-reassign
					module.default.layout = page => (
						<AppLayout
							key={component}
							builderStore={builderStore}
							appModel={appModel}
							initialScroll={initialScroll}
						>
							{page}
						</AppLayout>
					)

					return module
				})
			},
			setup({ el, App: InertiaApp, props }) {
				const generateClassName = () =>
					createGenerateClassName({
						productionPrefix: 'score-app-',
					})

				ReactDOM.render(
					<ErrorBoundary>
						<StylesProvider generateClassName={generateClassName()}>
							<BrowserRouter>
								<InertiaApp {...props} initialPage={window.pageData} />
							</BrowserRouter>
						</StylesProvider>
					</ErrorBoundary>,
					el,
				)
			},
		})
	}

	setState(state) {
		this.state = { ...this.state, ...state }
		this.handleMount(state)
	}

	addBugsnagMetaData = (event, data) => {
		Bugsnag.addMetadata(event, data)
	}

	handleMount(state) {
		const { active = false, version, initialScroll } = state
		if (!active) ReactDOM.unmountComponentAtNode(this.root)
		else {
			this.initApp(version, initialScroll)
		}
	}
}

const isBuilderMode = checkIsBuilderMode(window.pageData.props.data.mode)
if (isBuilderMode) {
	window.FrameState = new FrameState({ active: false })
	window.history.pushState = () => {}

	window.history.replaceState = () => {}

	class FakeUrl extends window.URL {
		constructor() {
			// eslint-disable-next-line no-param-reassign
			super('http://scoreapp.local')
		}
	}

	window.URL = FakeUrl
	window.addEventListener('unload', () => {
		window.FrameState.setState({ active: false })
	})
} else {
	const run = () => {
		window.FrameState = new FrameState({ active: true, version: BUILDER_VERSION })
	}

	run()
}
